import { TimerTextStack } from '../../components/display/TimerTextStack';
import { currentBlinds, formatForcedBets } from '../../lib/forcedBets';
import { tournamentDuration } from '../../lib/tournamentDuration';
import { TimerCountdown } from '../../components/display/TimerCountdown';
import { nextBreakSeconds } from '../../lib/nextBreakCountdown';
import { TimerMarquee } from '../../components/display/TimerMarquee';
import { TimerHeader, FittedText } from '../../components/display/TimerHeader';
import { TimerStatCard as StatCard } from '../../components/display/TimerStatCard';
import { FittedLines } from '../../components/display/FittedLines';
import { formatTimerClock } from '../../lib/timerClockFormat';
import { adaptTimerHeaderLayout, DEFAULT_TIMER_LAYOUT } from '../../lib/timerHeaderLayout';
import { GridLines } from '../../components/display/GridLines';
import { gridRect } from '../../lib/gridGeometry';
/**
 * @page TimerPreviewPage
 * @description 計時器佈局編輯器 + TV 預覽
 * 支援拖拉 + 調整大小 + 側邊欄資訊 + 跑馬燈 + 字體大小調整
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ResponsiveGridLayout, noCompactor } from 'react-grid-layout';
import { transformStrategy } from 'react-grid-layout/core';
import { fitTvCanvas, tvMetrics, tvFontSize } from '../../lib/tvMetrics';
import { formatTime } from '../../lib/format';
import type { Layout as RGLLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { getDisplayLevel } from '@pokerroom/types';
import type { BlindStructureDto, BlindStructureLevelDto, TournamentType } from '@pokerroom/types';
import { api } from '../../lib/api.ts';
import { THEMES, isValidTheme, type TimerTheme, type ThemeTokens } from '../../lib/timerThemes';
import { useSiteName } from '../../hooks/useSiteName';
import { unifyTimerLabels, TIMER_LABEL_KEYS } from '../../lib/timerLabelSettings';
import {
  TIMER_AUDIO_EVENTS,
  normalizeTimerAudio,
  timerAudioFromOptions,
  type TimerAudioConfig,
  type TimerAudioSound,
  type TimerAudioVolume,
} from '../../lib/timerAudioConfig';
import { playTimerTransitionSound } from '../../lib/timerTransitionSound';
import { timeoutSignal } from '../../lib/timeoutSignal';

// ─── Venue Logo ──────────────────────────────────────────────────────────────

function useVenueLogo(): string | null {
  const [logo, setLogo] = useState<string | null>(null);
  useEffect(() => {
    fetch('/api/branding/tv_logo', { signal: timeoutSignal(10000) })
      .then((r) => (r.ok ? (r.json() as Promise<{ updatedAt?: string }>) : null))
      .then((d) => {
        if (d) setLogo(`/api/branding/tv_logo/file?v=${d.updatedAt ?? ''}`);
      })
      .catch((err) => console.warn('[TimerPreview] Failed to fetch logo:', err));
  }, []);
  return logo;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function fetchBlindStructures(): Promise<BlindStructureDto[]> {
  return api.get<BlindStructureDto[]>('/tournament/blind-structures');
}

const MONO_STYLE: React.CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace",
  fontVariantNumeric: 'tabular-nums',
};

// ─── Timer State ─────────────────────────────────────────────────────────────

interface TimerState {
  remaining: number;
  isRunning: boolean;
  levelIndex: number;
  elapsed: number;
}

// ─── Layout + Config ─────────────────────────────────────────────────────────

type LayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
};

interface LayoutSlot {
  layout: LayoutItem[];
  sideInfoLines: string[];
  marqueeText: string;
  fontSizes: Record<string, number>;
}

interface TimerLayoutConfig {
  theme: TimerTheme;
  layouts: Record<TimerTheme, LayoutSlot>;
  options: Record<string, unknown>;
}

const WIDGET_LABELS: Record<string, string> = {
  logo: 'Logo / Brand',
  tournamentName: 'Tournament Name',
  clock: 'Late Reg Countdown',
  level: 'Level',
  timer: 'Timer',
  blinds: 'Blinds / Ante / Next',
  entries: 'Players',
  avgStack: 'Avg Stack',
  prizePool: 'Prize Pool',
  nextLevel: 'Next Level',
  rebuyAddon: 'Rebuy / Addon',
  elapsed: 'Time Remaining / Elapsed',
  sideInfo: 'Side Info',
  marquee: 'Marquee',
};

const DEFAULT_FONT_SIZES: Record<string, number> = {
  logo: 48,
  logoLabel: 20,
  logoName: 20,
  clock: 30,
  lateReg: 20,
  level: 30,
  timer: 200,
  blinds: 60,
  blindsLabel: 20,
  ante: 40,
  nextInfo: 20,
  statLabel: 20,
  entries: 40,
  avgStack: 40,
  prizePool: 40,
  nextLevel: 40,
  rebuyAddon: 40,
  elapsed: 40,
  sideInfo: 26,
  marquee: 26,
};

const FONT_SIZE_LABELS: Record<string, string> = {
  timer: 'Timer',
  blinds: 'Blinds',
  ante: 'Ante',
  level: 'Level',
  logoName: 'Tournament Name',
  logoLabel: 'Brand Label',
  clock: 'Clock',
  statLabel: 'Label Size',
  entries: 'Players',
  avgStack: 'Avg Stack',
  prizePool: 'Prize Pool',
  nextLevel: 'Next Level',
  rebuyAddon: 'Rebuy/Addon',
  elapsed: 'Time Remaining / Elapsed',
  sideInfo: 'Side Info',
  marquee: 'Marquee',
  nextInfo: 'Next Blinds Info',
};

const DEFAULT_LAYOUT: LayoutItem[] = DEFAULT_TIMER_LAYOUT;

function makeDefaultSlot(themeName: TimerTheme): LayoutSlot {
  return {
    layout: DEFAULT_LAYOUT,
    sideInfoLines: [],
    marqueeText: '',
    fontSizes: unifyTimerLabels(
      { ...DEFAULT_FONT_SIZES, ...(THEMES[themeName].defaultFontSizes ?? {}) },
      themeName,
    ),
  };
}

const DEFAULT_CONFIG: TimerLayoutConfig = {
  theme: 'custom',
  options: { timerAudio: normalizeTimerAudio(undefined) },
  layouts: {
    custom: makeDefaultSlot('custom'),
    pokerfans: makeDefaultSlot('pokerfans'),
  },
};

const STORAGE_KEY = 'zipa-timer-layout-config';

function isValidLayout(arr: unknown[]): boolean {
  return arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null && 'i' in arr[0];
}

function sanitizeSlot(s: unknown, theme: TimerTheme): LayoutSlot {
  const o = (s ?? {}) as Partial<LayoutSlot>;
  const layout = Array.isArray(o.layout) && isValidLayout(o.layout) ? o.layout : DEFAULT_LAYOUT;
  return {
    layout: adaptTimerHeaderLayout(layout),
    sideInfoLines: Array.isArray(o.sideInfoLines) ? o.sideInfoLines : [],
    marqueeText: typeof o.marqueeText === 'string' ? o.marqueeText : '',
    fontSizes: {
      ...makeDefaultSlot(theme).fontSizes,
      ...unifyTimerLabels(o.fontSizes ?? {}, theme),
    },
  };
}

export function migrateConfig(raw: unknown): TimerLayoutConfig {
  const r = (raw ?? {}) as {
    theme?: unknown;
    layouts?: unknown;
    layout?: unknown;
    options?: unknown;
  };
  const theme: TimerTheme = isValidTheme(r.theme) ? r.theme : 'custom';
  const options =
    r.options && typeof r.options === 'object' && !Array.isArray(r.options)
      ? (r.options as Record<string, unknown>)
      : {};
  const normalizedOptions = { ...options, timerAudio: timerAudioFromOptions(options) };
  // New shape: nested layouts map
  if (r.layouts && typeof r.layouts === 'object') {
    const ls = r.layouts as Record<string, unknown>;
    return {
      theme,
      options: normalizedOptions,
      layouts: {
        custom: sanitizeSlot(ls.custom, 'custom'),
        pokerfans: sanitizeSlot(ls.pokerfans, 'pokerfans'),
      },
    };
  }
  // Legacy shape: top-level layout = active theme's slot
  if (Array.isArray(r.layout) && isValidLayout(r.layout)) {
    const legacy = sanitizeSlot(r, theme);
    const other: TimerTheme = theme === 'custom' ? 'pokerfans' : 'custom';
    return {
      theme,
      options: normalizedOptions,
      layouts: { [theme]: legacy, [other]: makeDefaultSlot(other) } as Record<
        TimerTheme,
        LayoutSlot
      >,
    };
  }
  return { ...DEFAULT_CONFIG, theme, options: normalizedOptions };
}

function loadConfigFromLocal(): TimerLayoutConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return migrateConfig(JSON.parse(raw));
  } catch (err) {
    console.warn('[TimerPreview] Failed to load config from localStorage:', err);
  }
  return DEFAULT_CONFIG;
}

async function loadConfigFromAPI(): Promise<TimerLayoutConfig> {
  try {
    const data = await api.get<{ config?: unknown }>('/display/timer-layout');
    if (data?.config) return migrateConfig(data.config);
  } catch (err) {
    console.warn(
      '[TimerPreview] Failed to load config from API, falling back to localStorage:',
      err,
    );
  }
  return loadConfigFromLocal();
}

async function saveConfigToAPI(config: TimerLayoutConfig): Promise<boolean> {
  let shared = false;
  try {
    await api.put('/display/timer-layout', config);
    shared = true;
  } catch (err) {
    console.warn('[TimerPreview] Failed to save config to API, falling back to localStorage:', err);
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (err) {
    if (!shared) throw err;
    console.warn('[TimerPreview] Failed to cache saved config:', err);
  }
  return shared;
}

// ─── Widget Renderers ────────────────────────────────────────────────────────

interface WidgetProps {
  separateTitle: boolean;
  timer: TimerState;
  levels: BlindStructureLevelDto[];
  bsName: string;
  durationType: TournamentType;
  levelSecs: number;
  editMode: boolean;
  fontSizes: Record<string, number>;
  sideInfoLines: string[];
  marqueeText: string;
  logoUrl: string | null;
  theme: ThemeTokens;
  displayScale: number;
}

function WidgetContent({ id, props }: { id: string; props: WidgetProps }) {
  const siteName = useSiteName();
  const {
    timer,
    levels,
    bsName,
    durationType,
    levelSecs,
    fontSizes: fs,
    sideInfoLines,
    marqueeText,
    logoUrl,
    theme,
    displayScale,
  } = props;
  const sf = (px: number) => tvFontSize(px, displayScale);
  const currentLevel = levels[timer.levelIndex] ?? null;
  const nextLevel = levels[timer.levelIndex + 1] ?? null;
  const levelNo = currentLevel?.levelNo ?? 1;
  const displayLevel = getDisplayLevel(levels, levelNo);
  const sb = currentLevel?.smallBlind ?? 0;
  const bb = currentLevel?.bigBlind ?? 0;
  const ante = currentLevel?.ante ?? 0;
  const bba = currentLevel?.bba ?? 0;
  const isBreak = currentLevel?.isBreak ?? false;
  const blinds = currentBlinds(sb, bb, ante, bba, isBreak);
  const nextBreakSecs = nextBreakSeconds(levels, levelNo, timer.remaining, levelSecs);
  const isWarning = timer.remaining <= 60 && timer.isRunning && !isBreak;
  const nextDisplayLevel = nextLevel
    ? getDisplayLevel(levels, nextLevel.levelNo)
    : displayLevel + 1;
  const nextIsBreak = nextLevel?.isBreak ?? false;

  switch (id) {
    case 'logo':
    case 'tournamentName':
      return (
        <TimerHeader
          titleOnly={id === 'tournamentName'}
          separateTitle={props.separateTitle}
          logoUrl={logoUrl}
          siteName={siteName}
          name={bsName}
          theme={theme}
          fontSizes={fs}
          scale={props.displayScale}
        />
      );

    case 'lateReg': {
      // Calculate late reg countdown (preview: assume lateRegLevels = 4)
      const lateRegLvl = 4;
      let playCount = 0;
      for (let i = 0; i < levels.length; i++) {
        if (!levels[i]!.isBreak) playCount++;
        if (i === timer.levelIndex) break;
      }
      let lateRegSecs = 0;
      const lateRegOpen = playCount <= lateRegLvl;
      if (lateRegOpen) {
        lateRegSecs = timer.remaining;
        let cnt = playCount;
        for (let i = timer.levelIndex + 1; i < levels.length; i++) {
          if (!levels[i]!.isBreak) cnt++;
          if (cnt > lateRegLvl) break;
          lateRegSecs += levels[i]!.durationSecs ?? 120;
        }
      }
      return (
        <StatCard
          label="Late Reg"
          value={lateRegOpen ? formatTimerClock(lateRegSecs) : 'CLOSED'}
          fontSize={sf(fs.clock ?? 18)}
          labelSize={sf(fs.lateReg ?? 12)}
          theme={theme}
          labelColor={theme.primaryColor}
          valueColor={lateRegOpen ? theme.statValueColor : theme.timerWarningColor}
        />
      );
    }

    case 'level':
      return (
        <div className="flex items-center justify-center h-full min-w-0 min-h-0 p-1">
          <div
            className="rounded-xl w-full h-full min-w-0 min-h-0 px-2 py-1"
            style={{ backgroundColor: isBreak ? theme.accentBreakBg : theme.accentBg }}
          >
            <FittedText
              text={isBreak ? 'BREAK' : `LEVEL ${displayLevel}`}
              size={sf(fs.level ?? 28)}
              color={theme.statValueColor}
              className="tracking-wider"
            />
          </div>
        </div>
      );

    case 'timer':
      return (
        <TimerCountdown
          seconds={timer.remaining}
          isBreak={isBreak}
          isWarning={isWarning}
          theme={theme}
          fontSize={sf(fs.timer ?? 200)}
        />
      );

    case 'blinds':
      return (
        <div className="h-full w-full min-h-0 min-w-0 px-1" style={{ background: theme.blindsBg }}>
          <TimerTextStack
            rows={[
              {
                text: blinds.label,
                size: sf(fs.blindsLabel ?? 12),
                color: theme.blindsLabelColor,
                className: 'uppercase tracking-[0.3em]',
              },
              {
                text: blinds.value,
                size: sf(fs.blinds ?? 48),
                color: theme.blindsValueColor,
                className: 'font-black tracking-tight',
                style: { fontFamily: theme.fontFamily, fontVariantNumeric: 'tabular-nums' },
              },
              ...(nextLevel
                ? [
                    {
                      text: `Next: ${nextIsBreak ? 'BREAK' : `Lv ${nextDisplayLevel} — ${nextLevel.smallBlind?.toLocaleString()} / ${nextLevel.bigBlind?.toLocaleString()}`}${!nextIsBreak ? formatForcedBets(nextLevel.ante, nextLevel.bba) : ''}`,
                      size: sf(fs.nextInfo ?? 16),
                      color: theme.nextInfoColor,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      );

    case 'entries':
      return (
        <StatCard
          label="Players"
          value="-- / --"
          fontSize={sf(fs.entries ?? 28)}
          labelSize={sf(fs.statLabel ?? 11)}
          theme={theme}
        />
      );
    case 'avgStack':
      return (
        <StatCard
          label="Avg Stack"
          value="--"
          fontSize={sf(fs.avgStack ?? 28)}
          labelSize={sf(fs.statLabel ?? 11)}
          theme={theme}
        />
      );
    case 'prizePool':
      return (
        <StatCard
          label="Prize Pool"
          value="NT$ --"
          fontSize={sf(fs.prizePool ?? 28)}
          labelSize={sf(fs.statLabel ?? 11)}
          theme={theme}
          valueColor={theme.prizePoolValueColor}
        />
      );
    case 'nextLevel':
      return (
        <StatCard
          label={nextIsBreak ? 'Next' : 'Next Level'}
          value={nextIsBreak ? 'Break' : `Lv ${nextDisplayLevel}`}
          fontSize={sf(fs.nextLevel ?? 28)}
          labelSize={sf(fs.statLabel ?? 11)}
          theme={theme}
        />
      );
    case 'rebuyAddon':
      return (
        <StatCard
          label="Rebuy / Addon"
          value="0 / 0"
          fontSize={sf(fs.rebuyAddon ?? 28)}
          labelSize={sf(fs.statLabel ?? 11)}
          theme={theme}
        />
      );
    case 'elapsed': {
      const duration = tournamentDuration(durationType, levels, levelSecs, timer.elapsed, {
        level: levelNo,
        remaining: timer.remaining,
      });
      return (
        <StatCard
          label={duration.label}
          value={duration.value}
          fontSize={sf(fs.elapsed ?? 28)}
          labelSize={sf(fs.statLabel ?? 11)}
          theme={theme}
          secondary={
            nextBreakSecs !== null ? `Break in ${formatTimerClock(nextBreakSecs)}` : undefined
          }
        />
      );
    }

    case 'sideInfo':
      return (
        <div className="h-full min-h-0 min-w-0 px-4 py-3">
          <FittedLines
            lines={sideInfoLines.length ? sideInfoLines : ['Side info (edit to add)']}
            size={sf(fs.sideInfo ?? 16)}
            color={sideInfoLines.length ? theme.sideInfoColor : `${theme.sideInfoColor}66`}
          />
        </div>
      );

    case 'marquee':
      return (
        <div
          className="h-full w-full flex items-center overflow-hidden relative"
          style={{ backgroundColor: theme.marqueeBg }}
        >
          <TimerMarquee
            text={marqueeText}
            breakSeconds={nextBreakSecs}
            size={sf(fs.marquee ?? 24)}
            color={theme.marqueeColor}
          />
        </div>
      );

    default:
      return <div className="text-slate-500 text-xs p-2">{id}</div>;
  }
}

// ─── TV Layout Editor ──────────────────────────────────────────

function TVLayoutEditor({
  timer,
  levels,
  bsName,
  durationType,
  levelSecs,
  editMode,
  layout,
  onLayoutChange,
  fontSizes,
  sideInfoLines,
  marqueeText,
  logoUrl,
  selectedWidgetId,
  onSelectWidget,
  theme,
  displayScale,
  targetWidth,
  targetHeight,
  rowHeight,
}: Omit<WidgetProps, 'separateTitle'> & {
  targetWidth: number;
  targetHeight: number;
  rowHeight: number;
  layout: LayoutItem[];
  onLayoutChange: (layout: LayoutItem[]) => void;
  selectedWidgetId: string | null;
  onSelectWidget: (id: string | null) => void;
}) {
  const widgetProps: WidgetProps = {
    separateTitle: layout.some((item) => item.i === 'tournamentName'),
    timer,
    levels,
    bsName,
    durationType,
    levelSecs,
    editMode,
    fontSizes,
    sideInfoLines,
    marqueeText,
    logoUrl,
    theme,
    displayScale,
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const zoom = fitTvCanvas(size.width, size.height, targetWidth, targetHeight);
  // RGL 2.2.2's createScaledStrategy uses viewport-relative drag origins.
  // Retain its parent-relative fallback for this centered, offset canvas.
  const positionStrategy = useMemo(() => ({ ...transformStrategy, scale: zoom || 1 }), [zoom]);

  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.bgColor,
    backgroundImage: theme.bgPattern
      ? theme.bgImage
        ? `${theme.bgPattern}, ${theme.bgImage}`
        : theme.bgPattern
      : theme.bgImage,
    backgroundSize: theme.bgSize,
  };

  return (
    <div
      ref={containerRef}
      className="h-full min-h-0 text-slate-100 overflow-hidden relative bg-black"
      data-testid="tv-preview-viewport"
      onClick={(e) => {
        // Deselect when clicking empty area
        if (editMode && e.target === e.currentTarget) onSelectWidget(null);
      }}
    >
      {zoom > 0 && (
        <div
          data-testid="tv-preview-canvas"
          data-zoom={zoom}
          data-target-width={targetWidth}
          data-target-height={targetHeight}
          style={{
            ...containerStyle,
            position: 'absolute',
            width: targetWidth,
            height: targetHeight,
            left: (size.width - targetWidth * zoom) / 2,
            top: (size.height - targetHeight * zoom) / 2,
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            overflow: 'hidden',
          }}
        >
          <ResponsiveGridLayout
            width={targetWidth}
            className="layout"
            layouts={{ lg: layout as unknown as RGLLayout }}
            breakpoints={{ lg: 0 }}
            cols={{ lg: 12 }}
            rowHeight={rowHeight}
            autoSize={false}
            style={{ height: targetHeight }}
            positionStrategy={positionStrategy}
            dragConfig={{ enabled: editMode, handle: '.drag-handle' }}
            resizeConfig={{ enabled: editMode }}
            compactor={noCompactor}
            onLayoutChange={(newLayout: RGLLayout) => {
              const mapped: LayoutItem[] = (newLayout as unknown as LayoutItem[]).map((item) => {
                const def = DEFAULT_LAYOUT.find((d) => d.i === item.i);
                return {
                  i: item.i,
                  x: item.x,
                  y: item.y,
                  w: item.w,
                  h: item.h,
                  minW: def?.minW,
                  minH: def?.minH,
                };
              });
              onLayoutChange(mapped);
            }}
            margin={[0, 0]}
            containerPadding={[0, 0]}
          >
            {layout.map((item) => {
              const isSelected = editMode && selectedWidgetId === item.i;
              const itemStyle: React.CSSProperties = {
                backgroundColor: theme.widgetBg,
                boxShadow: editMode
                  ? isSelected
                    ? `0 0 0 2px ${theme.primaryColor}`
                    : `0 0 0 1px ${theme.widgetBorderActive}`
                  : undefined,
              };
              return (
                <div
                  key={item.i}
                  className="relative overflow-hidden"
                  style={itemStyle}
                  onClick={(e) => {
                    if (editMode) {
                      e.stopPropagation();
                      onSelectWidget(item.i);
                    }
                  }}
                >
                  {editMode && (
                    <div
                      className="drag-handle absolute top-0 left-0 right-0 h-5 flex items-center justify-center cursor-move z-20"
                      style={{
                        backgroundColor: isSelected
                          ? `${theme.primaryColor}55`
                          : `${theme.widgetBorderActive}55`,
                      }}
                    >
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider"
                        style={{ color: isSelected ? theme.primaryColor : theme.statValueColor }}
                      >
                        {WIDGET_LABELS[item.i] ?? item.i}
                      </span>
                    </div>
                  )}
                  <div className="h-full">
                    <WidgetContent id={item.i} props={widgetProps} />
                  </div>
                </div>
              );
            })}
          </ResponsiveGridLayout>
          {!editMode && (
            <GridLines
              rects={layout.map((item) => gridRect(item, targetWidth, rowHeight))}
              width={targetWidth}
              height={targetHeight}
              color={theme.widgetBorder}
              lineWidth={theme.borderWidthPx ?? 1}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Settings Panel ──────────────────────────────────────────────────────────

function SettingsPanel({
  fontSizes,
  onFontSizeChange,
  selectedWidgetId,
  layout,
  onWidgetSizeChange,
  audioConfig,
  onAudioChange,
}: {
  fontSizes: Record<string, number>;
  onFontSizeChange: (key: string, value: number) => void;
  selectedWidgetId: string | null;
  layout: LayoutItem[];
  onWidgetSizeChange: (id: string, field: 'w' | 'h' | 'x' | 'y', value: number) => void;
  audioConfig: TimerAudioConfig;
  onAudioChange: (value: TimerAudioConfig) => void;
}) {
  const selectedItem = selectedWidgetId ? layout.find((l) => l.i === selectedWidgetId) : null;

  return (
    <div className="h-full overflow-y-auto p-3 space-y-4 text-xs">
      {/* Selected Widget Size */}
      {selectedItem ? (
        <div className="bg-[#daa60b]/10 border border-[#daa60b]/30 rounded-lg p-3">
          <label className="block text-[#daa60b] font-bold mb-2 uppercase tracking-wider text-[10px]">
            {WIDGET_LABELS[selectedItem.i] ?? selectedItem.i}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-slate-400 text-[9px] block mb-0.5">X (col)</span>
              <input
                type="number"
                min={0}
                max={11}
                className="w-full bg-white/10 text-white rounded px-1.5 py-1 border border-white/20 text-[11px] outline-hidden focus:border-[#daa60b] text-center"
                value={selectedItem.x}
                onChange={(e) =>
                  onWidgetSizeChange(selectedItem.i, 'x', parseInt(e.target.value, 10) || 0)
                }
              />
            </div>
            <div>
              <span className="text-slate-400 text-[9px] block mb-0.5">Y (row)</span>
              <input
                type="number"
                min={0}
                max={20}
                className="w-full bg-white/10 text-white rounded px-1.5 py-1 border border-white/20 text-[11px] outline-hidden focus:border-[#daa60b] text-center"
                value={selectedItem.y}
                onChange={(e) =>
                  onWidgetSizeChange(selectedItem.i, 'y', parseInt(e.target.value, 10) || 0)
                }
              />
            </div>
            <div>
              <span className="text-slate-400 text-[9px] block mb-0.5">Width (cols)</span>
              <input
                type="number"
                min={selectedItem.minW ?? 1}
                max={12}
                className="w-full bg-white/10 text-white rounded px-1.5 py-1 border border-white/20 text-[11px] outline-hidden focus:border-[#daa60b] text-center"
                value={selectedItem.w}
                onChange={(e) =>
                  onWidgetSizeChange(selectedItem.i, 'w', parseInt(e.target.value, 10) || 1)
                }
              />
            </div>
            <div>
              <span className="text-slate-400 text-[9px] block mb-0.5">Height (rows)</span>
              <input
                type="number"
                min={selectedItem.minH ?? 1}
                max={13}
                className="w-full bg-white/10 text-white rounded px-1.5 py-1 border border-white/20 text-[11px] outline-hidden focus:border-[#daa60b] text-center"
                value={selectedItem.h}
                onChange={(e) =>
                  onWidgetSizeChange(selectedItem.i, 'h', parseInt(e.target.value, 10) || 1)
                }
              />
            </div>
          </div>
          <p className="text-slate-500 mt-2 text-[9px]">
            Grid: 12 columns. Drag edges to resize in TV grid units.
          </p>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
          <p className="text-slate-500 text-[10px]">Click a widget to edit its size</p>
        </div>
      )}

      <hr className="border-[#333]" />

      <p className="text-slate-500 text-[10px]">
        Side info + marquee: edit in Tournament Templates.
      </p>

      <section aria-label="Timer sounds" className="space-y-3">
        <h3 className="text-slate-300 font-bold">Timer sounds</h3>
        <p className="text-slate-400">First start: one chime (fixed)</p>
        <p className="text-slate-400">0% disables that event's reminder.</p>
        <p className="text-slate-500">Save, then reload the TV to apply sound settings.</p>
        {TIMER_AUDIO_EVENTS.map(([event, label]) => (
          <div key={event} className="space-y-1">
            <p className="text-slate-300">{label}</p>
            <div className="flex flex-wrap gap-1">
              <select
                aria-label={`${label} sound`}
                value={audioConfig[event].sound}
                className="min-h-9 bg-[#222] text-white rounded max-w-full"
                onChange={(e) =>
                  onAudioChange({
                    ...audioConfig,
                    [event]: { ...audioConfig[event], sound: e.target.value as TimerAudioSound },
                  })
                }
              >
                <option value="blinds-up">Blinds Up</option>
                <option value="countdown">Countdown</option>
              </select>
              <select
                aria-label={`${label} volume`}
                value={audioConfig[event].volume}
                className="min-h-9 bg-[#222] text-white rounded"
                onChange={(e) =>
                  onAudioChange({
                    ...audioConfig,
                    [event]: {
                      ...audioConfig[event],
                      volume: Number(e.target.value) as TimerAudioVolume,
                    },
                  })
                }
              >
                <option value={0.75}>75%</option>
                <option value={0.5}>50%</option>
                <option value={0.25}>25%</option>
                <option value={0}>0%</option>
              </select>
              <button
                type="button"
                aria-label={`Listen: ${label}`}
                className="min-h-9 px-2 rounded bg-white/10 text-white"
                onClick={() =>
                  void playTimerTransitionSound(
                    audioConfig[event].sound,
                    undefined,
                    undefined,
                    audioConfig[event].volume,
                  )
                }
              >
                Listen
              </button>
            </div>
          </div>
        ))}
      </section>

      {/* Font Sizes */}
      <div>
        <label className="block text-slate-300 font-bold mb-2 uppercase tracking-wider text-[10px]">
          Font Sizes (px)
        </label>
        <div className="space-y-1.5">
          {Object.entries(FONT_SIZE_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center gap-2">
              <span className="text-slate-400 w-24 truncate text-[10px]">{label}</span>
              <input
                type="number"
                aria-label={label}
                className="w-16 bg-white/10 text-white rounded px-1.5 py-0.5 border border-white/20 text-[11px] outline-hidden focus:border-[#daa60b] text-center"
                value={fontSizes[key] ?? DEFAULT_FONT_SIZES[key] ?? 16}
                onChange={(e) => onFontSizeChange(key, parseInt(e.target.value, 10) || 0)}
                min={6}
                max={400}
              />
              <span className="text-slate-600 text-[9px]">px</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function TimerPreviewPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [durationType, setDurationType] = useState<TournamentType>('TIMED');
  const logoUrl = useVenueLogo();

  const { data: blindStructures = [] } = useQuery({
    queryKey: ['blind-structures'],
    queryFn: fetchBlindStructures,
  });

  const [targetHeight, setTargetHeight] = useState(1080);
  const targetWidth = targetHeight === 1080 ? 1920 : 1280;
  const [bannerHeight, setBannerHeight] = useState(0);
  useEffect(() => {
    const banner = document.querySelector('.zipa-environment');
    if (!banner) return;
    const update = () => setBannerHeight(banner.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(banner);
    return () => observer.disconnect();
  }, []);
  const metrics = tvMetrics(targetHeight, bannerHeight);

  const [selectedBsId, setSelectedBsId] = useState<string>('');
  const [timer, setTimer] = useState<TimerState>({
    remaining: 0,
    isRunning: false,
    levelIndex: 0,
    elapsed: 0,
  });
  const [editMode, setEditMode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [theme, setTheme] = useState<TimerTheme>('custom');
  const [layoutsByTheme, setLayoutsByTheme] = useState<Record<TimerTheme, LayoutSlot>>({
    custom: makeDefaultSlot('custom'),
    pokerfans: makeDefaultSlot('pokerfans'),
  });
  const [options, setOptions] = useState<Record<string, unknown>>(DEFAULT_CONFIG.options);
  const [saveMessage, setSaveMessage] = useState('');
  const audioConfig = timerAudioFromOptions(options);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Derived: current active slot (read-only convenience)
  const currentSlot = layoutsByTheme[theme];
  const layout = currentSlot.layout;
  const sideInfoLines = currentSlot.sideInfoLines;
  const marqueeText = currentSlot.marqueeText;
  const fontSizes = currentSlot.fontSizes;

  // Patch helper: update fields of the currently-active theme's slot
  const patchActiveSlot = useCallback(
    (patch: Partial<LayoutSlot>) => {
      setLayoutsByTheme((prev) => ({ ...prev, [theme]: { ...prev[theme], ...patch } }));
    },
    [theme],
  );

  // Load config from API on mount
  useEffect(() => {
    loadConfigFromAPI()
      .then((cfg) => {
        setTheme(cfg.theme);
        setLayoutsByTheme(cfg.layouts);
        setOptions(cfg.options);
      })
      .catch((err) => console.warn('Failed to load timer config:', err.message));
  }, []);

  const selectedBs = blindStructures.find((bs) => bs.id === selectedBsId);
  const levels: BlindStructureLevelDto[] = useMemo(() => selectedBs?.levels ?? [], [selectedBs]);

  useEffect(() => {
    if (blindStructures.length > 0 && !selectedBsId) {
      setSelectedBsId(blindStructures[0]!.id);
    }
  }, [blindStructures, selectedBsId]);

  useEffect(() => {
    if (!selectedBs) return;
    const firstLevel = selectedBs.levels[0];
    const dur = firstLevel?.durationSecs ?? selectedBs.levelSecs;
    setTimer({ remaining: dur, isRunning: false, levelIndex: 0, elapsed: 0 });
  }, [selectedBsId, selectedBs]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!timer.isRunning) return;
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev.remaining <= 1) {
          const nextIdx = prev.levelIndex + 1;
          if (nextIdx >= levels.length) {
            return { ...prev, remaining: 0, isRunning: false };
          }
          const nextLvl = levels[nextIdx];
          const dur = nextLvl?.durationSecs ?? selectedBs?.levelSecs ?? 0;
          return { ...prev, remaining: dur, levelIndex: nextIdx, elapsed: prev.elapsed + 1 };
        }
        return { ...prev, remaining: prev.remaining - 1, elapsed: prev.elapsed + 1 };
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timer.isRunning, levels, selectedBs?.levelSecs]);

  const handleLayoutChange = useCallback(
    (newLayout: LayoutItem[]) => {
      patchActiveSlot({ layout: newLayout });
    },
    [patchActiveSlot],
  );

  const buildConfig = useCallback(
    (): TimerLayoutConfig => ({
      theme,
      layouts: layoutsByTheme,
      options,
    }),
    [theme, layoutsByTheme, options],
  );

  const handleSave = useCallback(() => {
    saveConfigToAPI(buildConfig())
      .then((shared) => {
        setSaveMessage(
          shared
            ? 'Saved. Reload the TV to apply settings.'
            : 'Saved on this browser only. TV settings were not updated.',
        );
        setEditMode(false);
        setSelectedWidgetId(null);
      })
      .catch((err) => {
        setSaveMessage('Settings could not be saved. Please try again.');
        console.warn('Failed to save timer config:', err.message);
      });
  }, [buildConfig]);

  // Reset only resets the currently-active theme's slot (preserves the other theme's edits)
  const handleReset = useCallback(() => {
    setLayoutsByTheme((prev) => {
      const next = { ...prev, [theme]: makeDefaultSlot(theme) };
      saveConfigToAPI({ theme, layouts: next, options })
        .then((shared) => {
          setSaveMessage(
            shared
              ? 'Reset saved. Reload the TV to apply settings.'
              : 'Reset on this browser only. TV settings were not updated.',
          );
        })
        .catch((err) => {
          setSaveMessage('Settings could not be saved. Please try again.');
          console.warn('Failed to reset timer config:', err.message);
        });
      return next;
    });
  }, [theme, options]);

  const handleCancel = useCallback(() => {
    loadConfigFromAPI()
      .then((cfg) => {
        setTheme(cfg.theme);
        setLayoutsByTheme(cfg.layouts);
        setOptions(cfg.options);
      })
      .catch((err) => console.warn('Failed to reload timer config:', err.message));
    setEditMode(false);
    setSelectedWidgetId(null);
  }, []);

  const handleFontSizeChange = useCallback(
    (key: string, value: number) => {
      const size = key === 'statLabel' ? Math.max(6, Math.min(400, value)) : value;
      const next = { ...fontSizes, [key]: size };
      if (key === 'statLabel') for (const labelKey of TIMER_LABEL_KEYS) next[labelKey] = size;
      patchActiveSlot({ fontSizes: next });
    },
    [patchActiveSlot, fontSizes],
  );

  const handleWidgetSizeChange = useCallback(
    (id: string, field: 'w' | 'h' | 'x' | 'y', value: number) => {
      const newLayout = layout.map((item) => (item.i === id ? { ...item, [field]: value } : item));
      patchActiveSlot({ layout: newLayout });
    },
    [layout, patchActiveSlot],
  );

  const bsName = selectedBs?.name ?? t('nav.timerPreview');

  return (
    <div className="timer-preview-page h-full min-h-0 bg-[#111] flex flex-col overflow-hidden">
      {/* Top toolbar */}
      <div className="min-h-[44px] flex-wrap py-2 bg-[#1a1a1a] border-b border-[#333] flex items-center px-4 gap-3 shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="text-slate-400 hover:text-white text-sm flex items-center gap-1"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          {t('back')}
        </button>
        <div className="w-px h-5 bg-[#444]" />
        <h2 className="text-white text-sm font-bold">{t('nav.timerPreview')}</h2>

        <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2 [&_button]:min-h-11 [&_button]:text-sm">
          {/* Theme toggle — always visible (preview + edit modes) */}
          <div className="flex items-center bg-white/5 rounded p-0.5 border border-white/10">
            <button
              type="button"
              onClick={() => setTheme('custom')}
              className={`px-2.5 py-1 rounded text-xs font-bold transition-colors ${
                theme === 'custom' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
              title="Custom (gold)"
            >
              Custom
            </button>
            <button
              type="button"
              onClick={() => setTheme('pokerfans')}
              className={`px-2.5 py-1 rounded text-xs font-bold transition-colors ${
                theme === 'pokerfans'
                  ? 'bg-purple-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="PokerFans (purple/neon)"
            >
              PokerFans
            </button>
          </div>
          <div className="w-px h-5 bg-[#444]" />
          {editMode ? (
            <>
              {!layout.some((item) => item.i === 'tournamentName') && (
                <button
                  onClick={() => patchActiveSlot({ layout: adaptTimerHeaderLayout(layout) })}
                  disabled={adaptTimerHeaderLayout(layout) === layout}
                  title="Widen Logo / Brand to at least 6 columns to separate the tournament name"
                  className="px-3 py-1 text-xs text-white bg-white/10 disabled:opacity-50"
                >
                  Separate title
                </button>
              )}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`px-3 py-1 rounded text-xs font-bold flex items-center gap-1 ${showSettings ? 'bg-[#daa60b]/30 text-[#daa60b]' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
              >
                <span className="material-symbols-outlined text-sm">tune</span>
                Settings
              </button>
              <button
                onClick={handleReset}
                className="px-3 py-1 rounded bg-red-600/20 text-red-400 hover:bg-red-600/40 text-xs font-bold flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-sm">restart_alt</span>
                Reset
              </button>
              <button
                onClick={handleCancel}
                className="px-3 py-1 rounded bg-white/10 text-white/60 hover:bg-white/20 text-xs font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-500 text-xs font-bold flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-sm">save</span>
                Save
              </button>
            </>
          ) : (
            <>
              <div className="px-3 py-0.5 rounded-full bg-orange-500/20 text-orange-300 text-xs font-bold">
                PREVIEW
              </div>
              <button
                onClick={() => setEditMode(true)}
                className="px-3 py-1 rounded bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/40 text-xs font-bold flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-sm">edit</span>
                Edit Layout
              </button>
            </>
          )}
        </div>
      </div>

      <label className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 px-4 py-2 text-sm text-base-content bg-base-200 shrink-0 [&_select]:min-h-11 [&_select]:text-base [&_select]:max-w-full">
        TV viewport
        <select
          aria-label="TV viewport"
          value={targetHeight}
          onChange={(event) => setTargetHeight(Number(event.target.value))}
        >
          <option value={1080}>1920 × 1080</option>
          <option value={720}>1280 × 720</option>
        </select>
        <select
          aria-label="Preview tournament type"
          value={durationType}
          onChange={(event) => setDurationType(event.target.value as TournamentType)}
        >
          <option value="TIMED">Timed — Total Duration</option>
          <option value="ELIMINATION">Elimination — Elapsed</option>
        </select>
        <span className="text-xs">
          Preview fits available space; font sizes are TV pixels at 1080p.
        </span>
      </label>
      {saveMessage && (
        <p role="status" className="px-4 py-2 text-sm text-amber-200">
          {saveMessage}
        </p>
      )}
      {/* Main area: TV editor + optional settings panel */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* TV Layout Editor */}
        <div className="flex-1 min-w-0 min-h-0 relative overflow-hidden">
          <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded bg-green-600/80 text-white text-[10px] font-bold uppercase tracking-wider">
            {editMode ? 'EDITING LAYOUT' : 'TV Display'}
          </div>
          {editMode && (
            <div className="absolute top-2 left-[140px] z-10 px-2 py-0.5 rounded bg-yellow-600/80 text-white text-[10px] font-bold">
              Drag handles to move, edges to resize
            </div>
          )}
          <TVLayoutEditor
            targetWidth={targetWidth}
            targetHeight={metrics.contentHeight}
            rowHeight={metrics.rowHeight}
            displayScale={metrics.scale}
            timer={timer}
            levels={levels}
            bsName={bsName}
            durationType={durationType}
            levelSecs={selectedBs?.levelSecs ?? 0}
            editMode={editMode}
            layout={layout}
            onLayoutChange={handleLayoutChange}
            fontSizes={fontSizes}
            sideInfoLines={sideInfoLines}
            marqueeText={marqueeText}
            logoUrl={logoUrl}
            theme={THEMES[theme]}
            selectedWidgetId={selectedWidgetId}
            onSelectWidget={(id) => {
              setSelectedWidgetId(id);
              if (id && !showSettings) setShowSettings(true);
            }}
          />
        </div>

        {/* Settings Panel (right side, visible when editing + toggled) */}
        {editMode && showSettings && (
          <div className="w-64 bg-[#1a1a1a] border-l border-[#333] shrink-0">
            <SettingsPanel
              fontSizes={fontSizes}
              onFontSizeChange={handleFontSizeChange}
              selectedWidgetId={selectedWidgetId}
              layout={layout}
              onWidgetSizeChange={handleWidgetSizeChange}
              audioConfig={audioConfig}
              onAudioChange={(value) => setOptions((prev) => ({ ...prev, timerAudio: value }))}
            />
          </div>
        )}
      </div>
    </div>
  );
}
